/**
 * 矩形プレーン・多角形プレーン・ブレースを、ボクセルに焼かず閉形式で距離を出す。
 * GPU の可視性はレイ–OBB・レイ–三角柱・レイ–カプセル。距離場そのものは CPU テストとボクセル残り用。
 * 解析枠は AABB 最長辺が大きい entity から載せ、入らなかったプレーン・ブレースはボクセルへ回す。
 */

import { obbBounds, rectanglePlaneObb, type Obb } from './obb';
import { aabbIntersects, unionBounds, type Aabb } from './physics';
import {
  braceAsSlab,
  braceExtrudeOffset,
  bracePolyline,
  braceSegments,
  braceStripQuads,
  isFillBrace,
  isFillGeometry,
  isPathBrace,
  fillSideFace,
  fillSideFaceHoles,
  pathFaceHoles,
  pathLogicalFace,
  planeSlab,
  stairPlanes,
  tessellateSlab,
  vec,
} from './geometry';
import { hasClothBake } from './cloth';
import { hasRevolve } from './revolve';
import type { BraceGeometry, Entity, PlaneGeometry, Vec3 } from './types';
import { BLOCK_YAW_CLASS_COUNT, BLOCK_YAW_DEGREES, worldToLocalXZ, yawRadians } from './view-grid';
import { sdObb, volumeBounds, type VoxelVolume } from './voxel-sdf';

export const ANALYTIC_PLANE_CAPACITY = 48;
export const ANALYTIC_TRIANGLE_CAPACITY = 48;
export const ANALYTIC_BRACE_CAPACITY = 48;
export const ANALYTIC_BVH_BINS = 8;
export const ANALYTIC_BVH_PRIM_CAPACITY =
  ANALYTIC_PLANE_CAPACITY + ANALYTIC_TRIANGLE_CAPACITY + ANALYTIC_BRACE_CAPACITY;
/** 最長軸で均等分割したときのビン内上限。GLSL の内側ループをこれで抑える。 */
export const ANALYTIC_BVH_BIN_PRIM_MAX = 24;
export const ANALYTIC_PRIM_PLANE = 0;
export const ANALYTIC_PRIM_TRIANGLE = 1;
export const ANALYTIC_PRIM_BRACE = 2;
export const ANALYTIC_LATTICE_PAD = 1;

/** 開始面の自己交差を避けるスキップ。内部が負の薄い板はスキップ中でも遮る。 */
export const SDF_TRACE_SKIP = 0.02;
export const SDF_TRACE_HIT_EPS = 0.002;
/** 厚み 0 でも SDF では 10mm の板（内部が負）として扱う。描画・衝突の 5mm とは別。 */
export const ANALYTIC_PLANE_SDF_THICKNESS = 0.01;
export const ANALYTIC_PLANE_MIN_HALF = ANALYTIC_PLANE_SDF_THICKNESS / 2;
/** 断面が薄くても球トレースが飛び越えない最小半径。 */
export const ANALYTIC_BRACE_MIN_RADIUS = ANALYTIC_PLANE_MIN_HALF;

export interface AnalyticPlane {
  center: Vec3;
  half: Vec3;
  axes: [Vec3, Vec3, Vec3];
}

/** 非矩形プレーンを分割した三角柱。中面の 3 点と半厚。 */
export interface AnalyticTriangle {
  a: Vec3;
  b: Vec3;
  c: Vec3;
  half: number;
}

/** ブレースを内接するカプセル。端点は半球が元の始点・終点に接するよう内側へずらす。 */
export interface AnalyticBrace {
  a: Vec3;
  b: Vec3;
  radius: number;
}

export interface AnalyticBvhPrim {
  kind: number;
  index: number;
  bounds: Aabb;
}

export interface AnalyticBvhBin {
  bounds: Aabb;
  start: number;
  count: number;
}

export interface AnalyticBvh {
  bins: AnalyticBvhBin[];
  prims: AnalyticBvhPrim[];
}

export interface BlockLatticeField {
  sizeI: number;
  sizeK: number;
  sizeJ: number;
  i0: number[];
  k0: number[];
  j0: number[];
  yawMask: number;
  occupied: Uint8Array;
}

export function isAnalyticSdfEntity(entity: Entity): boolean {
  if (entity.type === 'brace' && hasClothBake(entity.geometry)) return false;
  if (entity.type === 'brace' && hasRevolve(entity.geometry)) return false;
  if (entity.type === 'brace') {
    if (isFillGeometry(entity.geometry)) {
      const slab = braceAsSlab(entity.geometry);
      return (
        rectanglePlaneObb(slab) !== null || analyticTrianglesFromGeometry(slab).length > 0
      );
    }
    return (
      analyticBracesFromGeometry(entity.geometry).length > 0 ||
      analyticTrianglesFromBrace(entity.geometry).length > 0
    );
  }
  if (entity.type === 'stair') return stairPlanes(entity.geometry).length > 0;
  return false;
}

export function yawCosSin(yawClass: number): { cos: number; sin: number } {
  const radians = yawRadians(BLOCK_YAW_DEGREES[yawClass] ?? 0);
  return { cos: Math.cos(radians), sin: Math.sin(radians) };
}

export function occupancyTexel(
  field: Pick<BlockLatticeField, 'sizeI' | 'sizeK'>,
  yaw: number,
  di: number,
  dk: number,
  dj: number
): { x: number; y: number } {
  return { x: di + yaw * field.sizeI, y: dk + dj * field.sizeK };
}

export function occupancyIndex(
  field: Pick<BlockLatticeField, 'sizeI' | 'sizeK' | 'sizeJ'>,
  yaw: number,
  di: number,
  dk: number,
  dj: number
): number {
  const { x, y } = occupancyTexel(field, yaw, di, dk, dj);
  return x + y * (field.sizeI * BLOCK_YAW_CLASS_COUNT);
}

export function occupancyTextureSize(field: Pick<BlockLatticeField, 'sizeI' | 'sizeK' | 'sizeJ'>): {
  width: number;
  height: number;
} {
  return {
    width: Math.max(1, field.sizeI * BLOCK_YAW_CLASS_COUNT),
    height: Math.max(1, field.sizeK * field.sizeJ),
  };
}

function localBoundsOfVolume(volume: VoxelVolume, yaw: number): Aabb {
  const { origin, extent } = volume;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const dx of [0, extent]) {
    for (const dz of [0, extent]) {
      const local = worldToLocalXZ(origin.x + dx, origin.z + dz, yaw);
      if (local.x < minX) minX = local.x;
      if (local.x > maxX) maxX = local.x;
      if (local.z < minZ) minZ = local.z;
      if (local.z > maxZ) maxZ = local.z;
    }
  }
  return {
    min: { x: minX, y: origin.y, z: minZ },
    max: { x: maxX, y: origin.y + extent, z: maxZ },
  };
}

export function latticeRangeForVolume(
  volume: VoxelVolume,
  yawClass: number
): { i0: number; k0: number; j0: number; sizeI: number; sizeK: number; sizeJ: number } {
  const yaw = BLOCK_YAW_DEGREES[yawClass] ?? 0;
  const box = localBoundsOfVolume(volume, yaw);
  const pad = ANALYTIC_LATTICE_PAD;
  const i0 = Math.floor(box.min.x) - pad;
  const k0 = Math.floor(box.min.y) - pad;
  const j0 = Math.floor(box.min.z) - pad;
  const i1 = Math.ceil(box.max.x) + pad;
  const k1 = Math.ceil(box.max.y) + pad;
  const j1 = Math.ceil(box.max.z) + pad;
  return {
    i0,
    k0,
    j0,
    sizeI: Math.max(1, i1 - i0),
    sizeK: Math.max(1, k1 - k0),
    sizeJ: Math.max(1, j1 - j0),
  };
}

export function emptyBlockLatticeField(volume: VoxelVolume): BlockLatticeField {
  let sizeI = 1;
  let sizeK = 1;
  let sizeJ = 1;
  const i0: number[] = [];
  const k0: number[] = [];
  const j0: number[] = [];
  for (let yaw = 0; yaw < BLOCK_YAW_CLASS_COUNT; yaw++) {
    const range = latticeRangeForVolume(volume, yaw);
    i0.push(range.i0);
    k0.push(range.k0);
    j0.push(range.j0);
    if (range.sizeI > sizeI) sizeI = range.sizeI;
    if (range.sizeK > sizeK) sizeK = range.sizeK;
    if (range.sizeJ > sizeJ) sizeJ = range.sizeJ;
  }
  const { width, height } = occupancyTextureSize({ sizeI, sizeK, sizeJ });
  return {
    sizeI,
    sizeK,
    sizeJ,
    i0,
    k0,
    j0,
    yawMask: 0,
    occupied: new Uint8Array(width * height),
  };
}

export function buildBlockLatticeField(
  volume: VoxelVolume,
  blocks: Iterable<{ yawClass: number; i: number; j: number; k: number }>
): BlockLatticeField {
  const field = emptyBlockLatticeField(volume);
  for (const block of blocks) {
    const yaw = ((block.yawClass % BLOCK_YAW_CLASS_COUNT) + BLOCK_YAW_CLASS_COUNT) % BLOCK_YAW_CLASS_COUNT;
    const di = block.i - field.i0[yaw];
    const dk = block.k - field.k0[yaw];
    const dj = block.j - field.j0[yaw];
    if (di < 0 || dk < 0 || dj < 0 || di >= field.sizeI || dk >= field.sizeK || dj >= field.sizeJ) {
      continue;
    }
    field.occupied[occupancyIndex(field, yaw, di, dk, dj)] = 255;
    field.yawMask |= 1 << yaw;
  }
  return field;
}

function occupiedRel(field: BlockLatticeField, yaw: number, di: number, dk: number, dj: number): boolean {
  if (di < 0 || dk < 0 || dj < 0 || di >= field.sizeI || dk >= field.sizeK || dj >= field.sizeJ) {
    return false;
  }
  return field.occupied[occupancyIndex(field, yaw, di, dk, dj)] > 0;
}

function sdUnitCube(local: Vec3, i: number, k: number, j: number): number {
  return sdObb(local, unitCubeObb(i, k, j));
}

function unitCubeObb(i: number, k: number, j: number): Obb {
  return {
    center: { x: i + 0.5, y: k + 0.5, z: j + 0.5 },
    half: { x: 0.5, y: 0.5, z: 0.5 },
    axes: [
      { x: 1, y: 0, z: 0 },
      { x: 0, y: 1, z: 0 },
      { x: 0, y: 0, z: 1 },
    ],
  };
}

function worldToLocalPoint(point: Vec3, yawClass: number): Vec3 {
  const yaw = BLOCK_YAW_DEGREES[yawClass] ?? 0;
  const xz = worldToLocalXZ(point.x, point.z, yaw);
  return { x: xz.x, y: point.y, z: xz.z };
}

/** 3×3×3 近傍の占有セルは厳密な sdBox。空なら近傍境界までの保守的な距離。 */
export function sdBlockLattice(point: Vec3, field: BlockLatticeField): number {
  if (field.yawMask === 0) return 1e5;
  let best = 1e5;
  for (let yaw = 0; yaw < BLOCK_YAW_CLASS_COUNT; yaw++) {
    if ((field.yawMask & (1 << yaw)) === 0) continue;
    const local = worldToLocalPoint(point, yaw);
    const i0 = field.i0[yaw];
    const k0 = field.k0[yaw];
    const j0 = field.j0[yaw];
    const ci = Math.floor(local.x) - i0;
    const ck = Math.floor(local.y) - k0;
    const cj = Math.floor(local.z) - j0;
    let any = false;
    let localD = 1e5;
    for (let dj = -1; dj <= 1; dj++) {
      for (let dk = -1; dk <= 1; dk++) {
        for (let di = -1; di <= 1; di++) {
          const ri = ci + di;
          const rk = ck + dk;
          const rj = cj + dj;
          if (!occupiedRel(field, yaw, ri, rk, rj)) continue;
          any = true;
          localD = Math.min(localD, sdUnitCube(local, i0 + ri, k0 + rk, j0 + rj));
        }
      }
    }
    if (!any) {
      const cellMinX = Math.floor(local.x);
      const cellMinY = Math.floor(local.y);
      const cellMinZ = Math.floor(local.z);
      const toFace = Math.min(
        local.x - (cellMinX - 1),
        cellMinX + 2 - local.x,
        local.y - (cellMinY - 1),
        cellMinY + 2 - local.y,
        local.z - (cellMinZ - 1),
        cellMinZ + 2 - local.z
      );
      localD = Math.max(toFace, 0);
    }
    if (localD < best) best = localD;
  }
  return best;
}

/** 厚み 0 も ANALYTIC_PLANE_SDF_THICKNESS の板にする。内部が負なので球トレースが飛び越えない。 */
export function analyticPlaneFromGeometry(geometry: PlaneGeometry): AnalyticPlane | null {
  const obb = rectanglePlaneObb(geometry);
  if (!obb) return null;
  return {
    center: obb.center,
    half: {
      x: obb.half.x,
      y: obb.half.y,
      z: Math.max(obb.half.z, ANALYTIC_PLANE_MIN_HALF),
    },
    axes: obb.axes,
  };
}

export function sdAnalyticPlane(point: Vec3, plane: AnalyticPlane): number {
  return sdObb(point, plane);
}

function triangleAreaSq(a: Vec3, b: Vec3, c: Vec3): number {
  const n = vec.cross(vec.sub(b, a), vec.sub(c, a));
  return n.x * n.x + n.y * n.y + n.z * n.z;
}

/** 矩形は OBB に任せる。それ以外は耳切りした三角柱。 */
export function analyticTrianglesFromGeometry(geometry: PlaneGeometry): AnalyticTriangle[] {
  if (geometry.sideHoles && geometry.sideHoles.length > 0) {
    const slab = planeSlab(geometry);
    if (!slab) return [];
    const triangles: AnalyticTriangle[] = [];
    triangles.push(
      ...analyticTrianglesFromGeometry({
        points: slab.start,
        thickness: 0,
        normal: slab.normal,
        ...(geometry.holes && geometry.holes.length > 0 ? { holes: geometry.holes } : {}),
      })
    );
    const endHoles = geometry.holes?.map((hole) => hole.map((p) => vec.add(p, slab.offset)));
    triangles.push(
      ...analyticTrianglesFromGeometry({
        points: slab.start.map((p) => vec.add(p, slab.offset)),
        thickness: 0,
        normal: slab.normal,
        ...(endHoles && endHoles.length > 0 ? { holes: endHoles } : {}),
      })
    );
    for (let i = 0; i < geometry.points.length; i++) {
      const face = fillSideFace(geometry, i);
      if (!face) continue;
      const holes = fillSideFaceHoles(geometry, face);
      triangles.push(
        ...analyticTrianglesFromGeometry({
          points: face.points,
          thickness: 0,
          normal: face.normal,
          ...(holes.length > 0 ? { holes } : {}),
        })
      );
    }
    return triangles;
  }
  if (rectanglePlaneObb(geometry) !== null) return [];
  const tessellated = tessellateSlab(geometry);
  if (!tessellated) return [];
  const { slab, origin, tangent, bitangent, vertices2, indices } = tessellated;
  const midOffset = vec.scale(slab.offset, 0.5);
  const half = Math.max(vec.length(slab.offset) / 2, ANALYTIC_PLANE_MIN_HALF);
  const triangles: AnalyticTriangle[] = [];
  for (let i = 0; i < indices.length; i += 3) {
    const toWorld = (index: number): Vec3 => {
      const p = vertices2[index];
      return vec.add(origin, vec.add(vec.scale(tangent, p.x), vec.scale(bitangent, p.y)));
    };
    const a = vec.add(toWorld(indices[i]), midOffset);
    const b = vec.add(toWorld(indices[i + 1]), midOffset);
    const c = vec.add(toWorld(indices[i + 2]), midOffset);
    if (triangleAreaSq(a, b, c) < 1e-16) continue;
    triangles.push({ a, b, c, half });
  }
  return triangles;
}

function sdTriangle2(px: number, py: number, ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  const e0x = bx - ax;
  const e0y = by - ay;
  const v0x = px - ax;
  const v0y = py - ay;
  const e1x = cx - bx;
  const e1y = cy - by;
  const v1x = px - bx;
  const v1y = py - by;
  const e2x = ax - cx;
  const e2y = ay - cy;
  const v2x = px - cx;
  const v2y = py - cy;
  const d00 = e0x * e0x + e0y * e0y;
  const d11 = e1x * e1x + e1y * e1y;
  const d22 = e2x * e2x + e2y * e2y;
  const h0 = d00 > 1e-16 ? Math.min(1, Math.max(0, (v0x * e0x + v0y * e0y) / d00)) : 0;
  const h1 = d11 > 1e-16 ? Math.min(1, Math.max(0, (v1x * e1x + v1y * e1y) / d11)) : 0;
  const h2 = d22 > 1e-16 ? Math.min(1, Math.max(0, (v2x * e2x + v2y * e2y) / d22)) : 0;
  const pq0x = v0x - e0x * h0;
  const pq0y = v0y - e0y * h0;
  const pq1x = v1x - e1x * h1;
  const pq1y = v1y - e1y * h1;
  const pq2x = v2x - e2x * h2;
  const pq2y = v2y - e2y * h2;
  const s = Math.sign(e0x * e2y - e0y * e2x) || 1;
  const dx = Math.min(pq0x * pq0x + pq0y * pq0y, pq1x * pq1x + pq1y * pq1y, pq2x * pq2x + pq2y * pq2y);
  const dy = Math.min(s * (v0x * e0y - v0y * e0x), s * (v1x * e1y - v1y * e1x), s * (v2x * e2y - v2y * e2x));
  return -Math.sqrt(dx) * Math.sign(dy);
}

export function sdAnalyticTriangle(point: Vec3, triangle: AnalyticTriangle): number {
  const ab = vec.sub(triangle.b, triangle.a);
  const ac = vec.sub(triangle.c, triangle.a);
  const normal = vec.normalize(vec.cross(ab, ac));
  if (vec.length(normal) < 1e-9) return 1e5;
  const tangent = vec.normalize(ab);
  const bitangent = vec.cross(normal, tangent);
  const p = vec.sub(point, triangle.a);
  const d2 = sdTriangle2(
    vec.dot(p, tangent),
    vec.dot(p, bitangent),
    0,
    0,
    vec.dot(ab, tangent),
    vec.dot(ab, bitangent),
    vec.dot(ac, tangent),
    vec.dot(ac, bitangent)
  );
  const w0 = d2;
  const w1 = Math.abs(vec.dot(p, normal)) - triangle.half;
  const ox = w0 > 0 ? w0 : 0;
  const oy = w1 > 0 ? w1 : 0;
  return Math.min(Math.max(w0, w1), 0) + Math.hypot(ox, oy);
}

function considerTriangleHit(t: number | null, t0: { v: number }, t1: { v: number }): void {
  if (t === null) return;
  if (t < t0.v) t0.v = t;
  if (t > t1.v) t1.v = t;
}

function rayTriangleT(origin: Vec3, direction: Vec3, a: Vec3, b: Vec3, c: Vec3): number | null {
  const e1x = b.x - a.x;
  const e1y = b.y - a.y;
  const e1z = b.z - a.z;
  const e2x = c.x - a.x;
  const e2y = c.y - a.y;
  const e2z = c.z - a.z;
  const px = direction.y * e2z - direction.z * e2y;
  const py = direction.z * e2x - direction.x * e2z;
  const pz = direction.x * e2y - direction.y * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (Math.abs(det) < 1e-12) return null;
  const inv = 1 / det;
  const tx = origin.x - a.x;
  const ty = origin.y - a.y;
  const tz = origin.z - a.z;
  const u = (tx * px + ty * py + tz * pz) * inv;
  if (u < 0 || u > 1) return null;
  const qx = ty * e1z - tz * e1y;
  const qy = tz * e1x - tx * e1z;
  const qz = tx * e1y - ty * e1x;
  const v = (direction.x * qx + direction.y * qy + direction.z * qz) * inv;
  if (v < 0 || u + v > 1) return null;
  return (e2x * qx + e2y * qy + e2z * qz) * inv;
}

function trianglePrismInterval(
  origin: Vec3,
  direction: Vec3,
  triangle: AnalyticTriangle
): { t0: number; t1: number } | null {
  const n = vec.normalize(vec.cross(vec.sub(triangle.b, triangle.a), vec.sub(triangle.c, triangle.a)));
  if (vec.length(n) < 1e-9) return null;
  const off = vec.scale(n, triangle.half);
  const a0 = vec.add(triangle.a, off);
  const b0 = vec.add(triangle.b, off);
  const c0 = vec.add(triangle.c, off);
  const a1 = vec.sub(triangle.a, off);
  const b1 = vec.sub(triangle.b, off);
  const c1 = vec.sub(triangle.c, off);
  const t0 = { v: Infinity };
  const t1 = { v: -Infinity };
  considerTriangleHit(rayTriangleT(origin, direction, a0, b0, c0), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, a1, c1, b1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, a0, b0, b1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, a0, b1, a1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, b0, c0, c1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, b0, c1, b1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, c0, a0, a1), t0, t1);
  considerTriangleHit(rayTriangleT(origin, direction, c0, a1, c1), t0, t1);
  if (t0.v > t1.v) return null;
  return { t0: t0.v, t1: t1.v };
}

/** レイが三角柱に外から入る時刻。開始点の自己交差は null。 */
export function rayTrianglePrismEnter(
  origin: Vec3,
  direction: Vec3,
  triangle: AnalyticTriangle,
  tMin: number,
  tMax: number
): number | null {
  const hit = trianglePrismInterval(origin, direction, triangle);
  if (!hit) return null;
  if (hit.t1 < tMin || hit.t0 > tMax) return null;
  if (hit.t0 < tMin) return null;
  return hit.t0;
}

/**
 * 断面の短い辺に内接するカプセル。端が半球なので sdCapsule（線分距離 − 半径）で足りる。
 * 長さが直径以下なら中点の球になる。
 */
export function analyticBraceFromSegment(
  start: Vec3,
  end: Vec3,
  thickness: number
): AnalyticBrace | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  const length = Math.hypot(dx, dy, dz);
  if (length < 1e-9) return null;
  const radius = Math.max(thickness * 0.5, ANALYTIC_BRACE_MIN_RADIUS);
  const inset = Math.min(radius, length * 0.5);
  const s = inset / length;
  return {
    a: {
      x: start.x + dx * s,
      y: start.y + dy * s,
      z: start.z + dz * s,
    },
    b: {
      x: end.x - dx * s,
      y: end.y - dy * s,
      z: end.z - dz * s,
    },
    radius,
  };
}

export function analyticBraceFromGeometry(geometry: BraceGeometry): AnalyticBrace | null {
  return analyticBracesFromGeometry(geometry)[0] ?? null;
}

export function analyticTrianglesFromBrace(geometry: BraceGeometry): AnalyticTriangle[] {
  if (geometry.holes && geometry.holes.length > 0) {
    const { points, closed } = bracePolyline(geometry);
    const n = points.length;
    const count = closed ? n : n - 1;
    const triangles: AnalyticTriangle[] = [];
    for (let i = 0; i < count; i++) {
      const face = pathLogicalFace(geometry, i);
      if (!face) continue;
      const holes = pathFaceHoles(geometry, face);
      triangles.push(
        ...analyticTrianglesFromGeometry({
          points: face.points,
          thickness: geometry.thickness,
          normal: face.normal,
          ...(holes.length > 0 ? { holes } : {}),
        })
      );
    }
    return triangles;
  }
  const half = Math.max(geometry.thickness / 2, ANALYTIC_PLANE_MIN_HALF);
  const triangles: AnalyticTriangle[] = [];
  for (const quad of braceStripQuads(geometry)) {
    if (triangleAreaSq(quad.a, quad.b, quad.c) >= 1e-16) {
      triangles.push({ a: quad.a, b: quad.b, c: quad.c, half });
    }
    if (triangleAreaSq(quad.a, quad.c, quad.d) >= 1e-16) {
      triangles.push({ a: quad.a, b: quad.c, c: quad.d, half });
    }
  }
  return triangles;
}

export function analyticBracesFromGeometry(geometry: BraceGeometry): AnalyticBrace[] {
  if (vec.length(braceExtrudeOffset(geometry)) >= 1e-9) return [];
  const braces: AnalyticBrace[] = [];
  for (const segment of braceSegments(geometry)) {
    const brace = analyticBraceFromSegment(segment.start, segment.end, geometry.thickness);
    if (brace) braces.push(brace);
  }
  return braces;
}

export function sdCapsule(point: Vec3, a: Vec3, b: Vec3, radius: number): number {
  const pax = point.x - a.x;
  const pay = point.y - a.y;
  const paz = point.z - a.z;
  const bax = b.x - a.x;
  const bay = b.y - a.y;
  const baz = b.z - a.z;
  const baba = bax * bax + bay * bay + baz * baz;
  const h = baba > 1e-12 ? Math.min(1, Math.max(0, (pax * bax + pay * bay + paz * baz) / baba)) : 0;
  const dx = pax - bax * h;
  const dy = pay - bay * h;
  const dz = paz - baz * h;
  return Math.hypot(dx, dy, dz) - radius;
}

export function sdAnalyticBrace(point: Vec3, brace: AnalyticBrace): number {
  return sdCapsule(point, brace.a, brace.b, brace.radius);
}

function capsuleBounds(brace: AnalyticBrace): Aabb {
  return {
    min: {
      x: Math.min(brace.a.x, brace.b.x) - brace.radius,
      y: Math.min(brace.a.y, brace.b.y) - brace.radius,
      z: Math.min(brace.a.z, brace.b.z) - brace.radius,
    },
    max: {
      x: Math.max(brace.a.x, brace.b.x) + brace.radius,
      y: Math.max(brace.a.y, brace.b.y) + brace.radius,
      z: Math.max(brace.a.z, brace.b.z) + brace.radius,
    },
  };
}

function paddedPlaneBounds(plane: AnalyticPlane): Aabb {
  const bounds = obbBounds({
    ...plane,
    half: {
      x: Math.max(plane.half.x, ANALYTIC_PLANE_MIN_HALF),
      y: Math.max(plane.half.y, ANALYTIC_PLANE_MIN_HALF),
      z: Math.max(plane.half.z, ANALYTIC_PLANE_MIN_HALF),
    },
  });
  return bounds;
}

/** レイが OBB に外から入る時刻。開始点の自己交差は null。 */
export function rayObbEnter(
  origin: Vec3,
  direction: Vec3,
  plane: AnalyticPlane,
  tMin: number,
  tMax: number
): number | null {
  const half = {
    x: Math.max(plane.half.x, ANALYTIC_PLANE_MIN_HALF),
    y: Math.max(plane.half.y, ANALYTIC_PLANE_MIN_HALF),
    z: Math.max(plane.half.z, ANALYTIC_PLANE_MIN_HALF),
  };
  const px = origin.x - plane.center.x;
  const py = origin.y - plane.center.y;
  const pz = origin.z - plane.center.z;
  const localO = [
    px * plane.axes[0].x + py * plane.axes[0].y + pz * plane.axes[0].z,
    px * plane.axes[1].x + py * plane.axes[1].y + pz * plane.axes[1].z,
    px * plane.axes[2].x + py * plane.axes[2].y + pz * plane.axes[2].z,
  ];
  const localD = [
    direction.x * plane.axes[0].x + direction.y * plane.axes[0].y + direction.z * plane.axes[0].z,
    direction.x * plane.axes[1].x + direction.y * plane.axes[1].y + direction.z * plane.axes[1].z,
    direction.x * plane.axes[2].x + direction.y * plane.axes[2].y + direction.z * plane.axes[2].z,
  ];
  const h = [half.x, half.y, half.z];
  let tEnter = -Infinity;
  let tExit = Infinity;
  for (let axis = 0; axis < 3; axis++) {
    const o = localO[axis];
    const d = localD[axis];
    if (Math.abs(d) < 1e-12) {
      if (o < -h[axis] || o > h[axis]) return null;
      continue;
    }
    const inv = 1 / d;
    let t0 = (-h[axis] - o) * inv;
    let t1 = (h[axis] - o) * inv;
    if (t0 > t1) {
      const tmp = t0;
      t0 = t1;
      t1 = tmp;
    }
    if (t0 > tEnter) tEnter = t0;
    if (t1 < tExit) tExit = t1;
    if (tEnter > tExit) return null;
  }
  if (tExit < tMin || tEnter > tMax) return null;
  if (tEnter < tMin) return null;
  return tEnter;
}

function segmentSegmentDistanceSq(
  p1: Vec3,
  q1: Vec3,
  p2: Vec3,
  q2: Vec3
): number {
  const d1x = q1.x - p1.x;
  const d1y = q1.y - p1.y;
  const d1z = q1.z - p1.z;
  const d2x = q2.x - p2.x;
  const d2y = q2.y - p2.y;
  const d2z = q2.z - p2.z;
  const rx = p1.x - p2.x;
  const ry = p1.y - p2.y;
  const rz = p1.z - p2.z;
  const a = d1x * d1x + d1y * d1y + d1z * d1z;
  const e = d2x * d2x + d2y * d2y + d2z * d2z;
  const f = d2x * rx + d2y * ry + d2z * rz;
  let s: number;
  let t: number;
  if (a < 1e-12 && e < 1e-12) {
    return rx * rx + ry * ry + rz * rz;
  }
  if (a < 1e-12) {
    s = 0;
    t = Math.min(1, Math.max(0, f / e));
  } else {
    const c = d1x * rx + d1y * ry + d1z * rz;
    if (e < 1e-12) {
      t = 0;
      s = Math.min(1, Math.max(0, -c / a));
    } else {
      const b = d1x * d2x + d1y * d2y + d1z * d2z;
      const denom = a * e - b * b;
      s = Math.abs(denom) > 1e-12 ? Math.min(1, Math.max(0, (b * f - c * e) / denom)) : 0;
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = Math.min(1, Math.max(0, -c / a));
      } else if (t > 1) {
        t = 1;
        s = Math.min(1, Math.max(0, (b - c) / a));
      }
    }
  }
  const nx = p1.x + d1x * s - (p2.x + d2x * t);
  const ny = p1.y + d1y * s - (p2.y + d2y * t);
  const nz = p1.z + d1z * s - (p2.z + d2z * t);
  return nx * nx + ny * ny + nz * nz;
}

/** GPU `rayCapsule` と同じ近傍根。区間外は null。 */
export function rayCapsuleEnter(
  origin: Vec3,
  direction: Vec3,
  brace: AnalyticBrace,
  tMin: number,
  tMax: number
): number | null {
  const bax = brace.b.x - brace.a.x;
  const bay = brace.b.y - brace.a.y;
  const baz = brace.b.z - brace.a.z;
  const oax = origin.x - brace.a.x;
  const oay = origin.y - brace.a.y;
  const oaz = origin.z - brace.a.z;
  const baba = bax * bax + bay * bay + baz * baz;
  const bard = bax * direction.x + bay * direction.y + baz * direction.z;
  const baoa = bax * oax + bay * oay + baz * oaz;
  const rdoa = direction.x * oax + direction.y * oay + direction.z * oaz;
  const oaoa = oax * oax + oay * oay + oaz * oaz;
  const ra = brace.radius;
  const a = baba - bard * bard;
  let b = baba * rdoa - baoa * bard;
  let c = baba * oaoa - baoa * baoa - ra * ra * baba;
  let h = b * b - a * c;
  if (h < 0) return null;
  let t = (-b - Math.sqrt(h)) / Math.max(a, 1e-8);
  const y = baoa + t * bard;
  if (y <= 0 || y >= baba) {
    const ocx = y <= 0 ? oax : origin.x - brace.b.x;
    const ocy = y <= 0 ? oay : origin.y - brace.b.y;
    const ocz = y <= 0 ? oaz : origin.z - brace.b.z;
    b = direction.x * ocx + direction.y * ocy + direction.z * ocz;
    c = ocx * ocx + ocy * ocy + ocz * ocz - ra * ra;
    h = b * b - c;
    if (h < 0) return null;
    t = -b - Math.sqrt(h);
  }
  if (t < tMin || t > tMax) return null;
  return t;
}

/** 有限レイがカプセルと重なるか。開始点が内側でも true。 */
export function rayCapsuleHits(
  origin: Vec3,
  direction: Vec3,
  brace: AnalyticBrace,
  tMin: number,
  tMax: number
): boolean {
  if (tMax <= tMin) return false;
  const p1 = {
    x: origin.x + direction.x * tMin,
    y: origin.y + direction.y * tMin,
    z: origin.z + direction.z * tMin,
  };
  const q1 = {
    x: origin.x + direction.x * tMax,
    y: origin.y + direction.y * tMax,
    z: origin.z + direction.z * tMax,
  };
  const r = brace.radius;
  return segmentSegmentDistanceSq(p1, q1, brace.a, brace.b) <= r * r;
}

export function aabbMaxExtent(box: Aabb): number {
  return Math.max(box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
}

export interface AnalyticSdfCapacities {
  planes?: number;
  triangles?: number;
  braces?: number;
}

export interface AnalyticSdfLeftover {
  planes: AnalyticPlane[];
  triangles: AnalyticTriangle[];
  braces: AnalyticBrace[];
  ids: string[];
}

export interface AnalyticSdfCollection {
  planes: AnalyticPlane[];
  triangles: AnalyticTriangle[];
  braces: AnalyticBrace[];
  packedIds: string[];
  leftover: AnalyticSdfLeftover;
}

interface AnalyticSdfCandidate {
  id: string;
  extent: number;
  planes: AnalyticPlane[];
  triangles: AnalyticTriangle[];
  braces: AnalyticBrace[];
}

function analyticPrimitivesInVolume(entity: Entity, area: Aabb): {
  planes: AnalyticPlane[];
  triangles: AnalyticTriangle[];
  braces: AnalyticBrace[];
} {
  const planes: AnalyticPlane[] = [];
  const triangles: AnalyticTriangle[] = [];
  const braces: AnalyticBrace[] = [];
  if (entity.type === 'brace' && hasClothBake(entity.geometry)) {
    return { planes, triangles, braces };
  }
  if (entity.type === 'brace' && hasRevolve(entity.geometry)) {
    return { planes, triangles, braces };
  }
  if (isFillBrace(entity) || entity.type === 'stair') {
    const geometries = isFillBrace(entity)
      ? [braceAsSlab(entity.geometry)]
      : stairPlanes(entity.geometry);
    for (const geometry of geometries) {
      const plane = analyticPlaneFromGeometry(geometry);
      if (plane) {
        if (aabbIntersects(paddedPlaneBounds(plane), area)) planes.push(plane);
        continue;
      }
      if (!isFillBrace(entity)) continue;
      for (const triangle of analyticTrianglesFromGeometry(geometry)) {
        if (aabbIntersects(triangleBounds(triangle), area)) triangles.push(triangle);
      }
    }
  } else if (isPathBrace(entity)) {
    for (const brace of analyticBracesFromGeometry(entity.geometry)) {
      if (aabbIntersects(capsuleBounds(brace), area)) braces.push(brace);
    }
    for (const triangle of analyticTrianglesFromBrace(entity.geometry)) {
      if (aabbIntersects(triangleBounds(triangle), area)) triangles.push(triangle);
    }
  }
  return { planes, triangles, braces };
}

/** 最長辺が大きい entity から、枠に全部入るものだけ載せる。溢れた entity は primitive を分けない。 */
export function collectAnalyticSdf(
  entities: Iterable<Entity>,
  skipIds: ReadonlySet<string>,
  volume: VoxelVolume,
  capacities: AnalyticSdfCapacities = {}
): AnalyticSdfCollection {
  const planeCapacity = capacities.planes ?? ANALYTIC_PLANE_CAPACITY;
  const triangleCapacity = capacities.triangles ?? ANALYTIC_TRIANGLE_CAPACITY;
  const braceCapacity = capacities.braces ?? ANALYTIC_BRACE_CAPACITY;
  const area = volumeBounds(volume);
  const candidates: AnalyticSdfCandidate[] = [];
  for (const entity of entities) {
    if (skipIds.has(entity.id)) continue;
    const pieces = analyticPrimitivesInVolume(entity, area);
    if (pieces.planes.length + pieces.triangles.length + pieces.braces.length === 0) continue;
    const union = unionBounds([
      ...pieces.planes.map(paddedPlaneBounds),
      ...pieces.triangles.map(triangleBounds),
      ...pieces.braces.map(capsuleBounds),
    ]);
    if (!union) continue;
    candidates.push({ id: entity.id, extent: aabbMaxExtent(union), ...pieces });
  }
  candidates.sort((a, b) => b.extent - a.extent || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const planes: AnalyticPlane[] = [];
  const triangles: AnalyticTriangle[] = [];
  const braces: AnalyticBrace[] = [];
  const packedIds: string[] = [];
  const leftover: AnalyticSdfLeftover = { planes: [], triangles: [], braces: [], ids: [] };
  const overflow: AnalyticSdfCandidate[] = [];
  for (const candidate of candidates) {
    if (
      planes.length + candidate.planes.length <= planeCapacity &&
      triangles.length + candidate.triangles.length <= triangleCapacity &&
      braces.length + candidate.braces.length <= braceCapacity
    ) {
      planes.push(...candidate.planes);
      triangles.push(...candidate.triangles);
      braces.push(...candidate.braces);
      packedIds.push(candidate.id);
    } else {
      overflow.push(candidate);
    }
  }
  for (const candidate of overflow) {
    leftover.ids.push(candidate.id);
    leftover.planes.push(...candidate.planes);
    leftover.triangles.push(...candidate.triangles);
    leftover.braces.push(...candidate.braces);
  }
  return { planes, triangles, braces, packedIds, leftover };
}

export function collectAnalyticPlanes(
  entities: Iterable<Entity>,
  skipIds: ReadonlySet<string>,
  volume: VoxelVolume,
  capacity = ANALYTIC_PLANE_CAPACITY
): { planes: AnalyticPlane[]; ids: string[] } {
  const collected = collectAnalyticSdf(entities, skipIds, volume, { planes: capacity });
  return { planes: collected.planes, ids: collected.packedIds };
}

function triangleBounds(triangle: AnalyticTriangle): Aabb {
  return {
    min: {
      x: Math.min(triangle.a.x, triangle.b.x, triangle.c.x) - triangle.half,
      y: Math.min(triangle.a.y, triangle.b.y, triangle.c.y) - triangle.half,
      z: Math.min(triangle.a.z, triangle.b.z, triangle.c.z) - triangle.half,
    },
    max: {
      x: Math.max(triangle.a.x, triangle.b.x, triangle.c.x) + triangle.half,
      y: Math.max(triangle.a.y, triangle.b.y, triangle.c.y) + triangle.half,
      z: Math.max(triangle.a.z, triangle.b.z, triangle.c.z) + triangle.half,
    },
  };
}

export function collectAnalyticTriangles(
  entities: Iterable<Entity>,
  skipIds: ReadonlySet<string>,
  volume: VoxelVolume,
  capacity = ANALYTIC_TRIANGLE_CAPACITY
): { triangles: AnalyticTriangle[]; ids: string[] } {
  const collected = collectAnalyticSdf(entities, skipIds, volume, { triangles: capacity });
  return { triangles: collected.triangles, ids: collected.packedIds };
}

export function collectAnalyticBraces(
  entities: Iterable<Entity>,
  skipIds: ReadonlySet<string>,
  volume: VoxelVolume,
  capacity = ANALYTIC_BRACE_CAPACITY
): { braces: AnalyticBrace[]; ids: string[] } {
  const collected = collectAnalyticSdf(entities, skipIds, volume, { braces: capacity });
  return { braces: collected.braces, ids: collected.packedIds };
}

export function packAnalyticPlanes(planes: readonly AnalyticPlane[], capacity = ANALYTIC_PLANE_CAPACITY): Float32Array {
  const data = new Float32Array(capacity * 4 * 4);
  const n = Math.min(planes.length, capacity);
  for (let i = 0; i < n; i++) {
    const plane = planes[i];
    const base = i * 4;
    data[base] = plane.center.x;
    data[base + 1] = plane.center.y;
    data[base + 2] = plane.center.z;
    data[base + 3] = plane.half.x;
    data[base + capacity * 4] = plane.half.y;
    data[base + capacity * 4 + 1] = plane.half.z;
    data[base + capacity * 4 + 2] = plane.axes[0].x;
    data[base + capacity * 4 + 3] = plane.axes[0].y;
    data[base + capacity * 8] = plane.axes[0].z;
    data[base + capacity * 8 + 1] = plane.axes[1].x;
    data[base + capacity * 8 + 2] = plane.axes[1].y;
    data[base + capacity * 8 + 3] = plane.axes[1].z;
    data[base + capacity * 12] = plane.axes[2].x;
    data[base + capacity * 12 + 1] = plane.axes[2].y;
    data[base + capacity * 12 + 2] = plane.axes[2].z;
  }
  return data;
}

export function packAnalyticTriangles(
  triangles: readonly AnalyticTriangle[],
  capacity = ANALYTIC_TRIANGLE_CAPACITY
): Float32Array {
  const data = new Float32Array(capacity * 3 * 4);
  const n = Math.min(triangles.length, capacity);
  for (let i = 0; i < n; i++) {
    const triangle = triangles[i];
    const base = i * 4;
    data[base] = triangle.a.x;
    data[base + 1] = triangle.a.y;
    data[base + 2] = triangle.a.z;
    data[base + 3] = triangle.half;
    data[base + capacity * 4] = triangle.b.x;
    data[base + capacity * 4 + 1] = triangle.b.y;
    data[base + capacity * 4 + 2] = triangle.b.z;
    data[base + capacity * 8] = triangle.c.x;
    data[base + capacity * 8 + 1] = triangle.c.y;
    data[base + capacity * 8 + 2] = triangle.c.z;
  }
  return data;
}

export function packAnalyticBraces(braces: readonly AnalyticBrace[], capacity = ANALYTIC_BRACE_CAPACITY): Float32Array {
  const data = new Float32Array(capacity * 2 * 4);
  const n = Math.min(braces.length, capacity);
  for (let i = 0; i < n; i++) {
    const brace = braces[i];
    const base = i * 4;
    data[base] = brace.a.x;
    data[base + 1] = brace.a.y;
    data[base + 2] = brace.a.z;
    data[base + 3] = brace.radius;
    data[base + capacity * 4] = brace.b.x;
    data[base + capacity * 4 + 1] = brace.b.y;
    data[base + capacity * 4 + 2] = brace.b.z;
  }
  return data;
}

const EMPTY_BVH_BOUNDS: Aabb = {
  min: { x: 0, y: 0, z: 0 },
  max: { x: 0, y: 0, z: 0 },
};

function aabbCentroid(box: Aabb): Vec3 {
  return {
    x: (box.min.x + box.max.x) * 0.5,
    y: (box.min.y + box.max.y) * 0.5,
    z: (box.min.z + box.max.z) * 0.5,
  };
}

function expandBounds(a: Aabb, b: Aabb): Aabb {
  return {
    min: {
      x: Math.min(a.min.x, b.min.x),
      y: Math.min(a.min.y, b.min.y),
      z: Math.min(a.min.z, b.min.z),
    },
    max: {
      x: Math.max(a.max.x, b.max.x),
      y: Math.max(a.max.y, b.max.y),
      z: Math.max(a.max.z, b.max.z),
    },
  };
}

function aabbSurfaceArea(box: Aabb): number {
  const dx = Math.max(0, box.max.x - box.min.x);
  const dy = Math.max(0, box.max.y - box.min.y);
  const dz = Math.max(0, box.max.z - box.min.z);
  return 2 * (dx * dy + dy * dz + dz * dx);
}

function comparePrims(a: AnalyticBvhPrim, b: AnalyticBvhPrim, axis: keyof Vec3): number {
  const d = aabbCentroid(a.bounds)[axis] - aabbCentroid(b.bounds)[axis];
  if (d !== 0) return d;
  return a.kind - b.kind || a.index - b.index;
}

/** 床＋壁のような直交スラブを同じビンに入れると AABB が部屋全体になるので、SAH で切る。 */
function sahPartition(items: AnalyticBvhPrim[]): { left: AnalyticBvhPrim[]; right: AnalyticBvhPrim[] } {
  let bestCost = Infinity;
  let bestMid = Math.floor(items.length / 2);
  let bestSorted = items;
  for (const axis of ['x', 'y', 'z'] as const) {
    const sorted = items.slice().sort((a, b) => comparePrims(a, b, axis));
    const rightUnions: Aabb[] = new Array(sorted.length);
    let right = sorted[sorted.length - 1].bounds;
    rightUnions[sorted.length - 1] = right;
    for (let i = sorted.length - 2; i >= 1; i--) {
      right = expandBounds(right, sorted[i].bounds);
      rightUnions[i] = right;
    }
    let left = sorted[0].bounds;
    for (let mid = 1; mid < sorted.length; mid++) {
      if (mid > 1) left = expandBounds(left, sorted[mid - 1].bounds);
      const cost = aabbSurfaceArea(left) * mid + aabbSurfaceArea(rightUnions[mid]) * (sorted.length - mid);
      if (cost < bestCost) {
        bestCost = cost;
        bestMid = mid;
        bestSorted = sorted;
      }
    }
  }
  return { left: bestSorted.slice(0, bestMid), right: bestSorted.slice(bestMid) };
}

function splitPrimsToBins(items: AnalyticBvhPrim[], binCount: number): AnalyticBvhPrim[][] {
  if (binCount <= 1) return [items];
  if (items.length === 0) return Array.from({ length: binCount }, () => []);
  if (items.length === 1) {
    return [items, ...Array.from({ length: binCount - 1 }, () => [])];
  }
  const { left, right } = sahPartition(items);
  const leftBins = Math.max(1, Math.min(binCount - 1, Math.round((binCount * left.length) / items.length)));
  return [
    ...splitPrimsToBins(left, leftBins),
    ...splitPrimsToBins(right, binCount - leftBins),
  ];
}

export function buildAnalyticBvh(
  planes: readonly AnalyticPlane[],
  triangles: readonly AnalyticTriangle[],
  braces: readonly AnalyticBrace[]
): AnalyticBvh {
  const items: AnalyticBvhPrim[] = [];
  const planeLimit = Math.min(planes.length, ANALYTIC_PLANE_CAPACITY);
  for (let i = 0; i < planeLimit; i++) {
    items.push({ kind: ANALYTIC_PRIM_PLANE, index: i, bounds: paddedPlaneBounds(planes[i]) });
  }
  const triangleLimit = Math.min(triangles.length, ANALYTIC_TRIANGLE_CAPACITY);
  for (let i = 0; i < triangleLimit; i++) {
    items.push({ kind: ANALYTIC_PRIM_TRIANGLE, index: i, bounds: triangleBounds(triangles[i]) });
  }
  const braceLimit = Math.min(braces.length, ANALYTIC_BRACE_CAPACITY);
  for (let i = 0; i < braceLimit; i++) {
    items.push({ kind: ANALYTIC_PRIM_BRACE, index: i, bounds: capsuleBounds(braces[i]) });
  }
  const groups = splitPrimsToBins(items, ANALYTIC_BVH_BINS);
  const prims: AnalyticBvhPrim[] = [];
  const bins: AnalyticBvhBin[] = groups.map((group) => {
    const start = prims.length;
    prims.push(...group);
    return {
      bounds: unionBounds(group.map((item) => item.bounds)) ?? EMPTY_BVH_BOUNDS,
      start,
      count: group.length,
    };
  });
  return { bins, prims };
}

export const ANALYTIC_ACCEL_WIDTH = ANALYTIC_BVH_PRIM_CAPACITY;
export const ANALYTIC_ACCEL_HEIGHT = 4;
export const ANALYTIC_ACCEL_ROW_BIN_MIN = 1;
export const ANALYTIC_ACCEL_ROW_BIN_MAX = 2;
export const ANALYTIC_ACCEL_ROW_PRIM = 3;

/** latticeOriginMap に BVH を載せる。行 0 は使わない。 */
export function packAnalyticAccelMap(bvh: AnalyticBvh): Float32Array {
  const data = new Float32Array(ANALYTIC_ACCEL_WIDTH * ANALYTIC_ACCEL_HEIGHT * 4);
  const row = ANALYTIC_ACCEL_WIDTH * 4;
  for (let i = 0; i < ANALYTIC_BVH_BINS; i++) {
    const bin = bvh.bins[i] ?? { bounds: EMPTY_BVH_BOUNDS, start: 0, count: 0 };
    const minBase = ANALYTIC_ACCEL_ROW_BIN_MIN * row + i * 4;
    const maxBase = ANALYTIC_ACCEL_ROW_BIN_MAX * row + i * 4;
    data[minBase] = bin.bounds.min.x;
    data[minBase + 1] = bin.bounds.min.y;
    data[minBase + 2] = bin.bounds.min.z;
    data[minBase + 3] = bin.start;
    data[maxBase] = bin.bounds.max.x;
    data[maxBase + 1] = bin.bounds.max.y;
    data[maxBase + 2] = bin.bounds.max.z;
    data[maxBase + 3] = bin.count;
  }
  const primRow = ANALYTIC_ACCEL_ROW_PRIM * row;
  const n = Math.min(bvh.prims.length, ANALYTIC_BVH_PRIM_CAPACITY);
  for (let i = 0; i < n; i++) {
    const prim = bvh.prims[i];
    data[primRow + i * 4] = prim.kind;
    data[primRow + i * 4 + 1] = prim.index;
  }
  return data;
}

function considerHit(t: number | null, best: { v: number }, anyHit: boolean): boolean {
  if (t === null) return false;
  if (t < best.v) best.v = t;
  return anyHit;
}

export function analyticBruteHitT(
  origin: Vec3,
  direction: Vec3,
  tMin: number,
  tMax: number,
  planes: readonly AnalyticPlane[],
  triangles: readonly AnalyticTriangle[],
  braces: readonly AnalyticBrace[],
  anyHit = false
): number {
  const best = { v: Infinity };
  for (const plane of planes) {
    if (considerHit(rayObbEnter(origin, direction, plane, tMin, tMax), best, anyHit)) return best.v;
  }
  for (const triangle of triangles) {
    if (considerHit(rayTrianglePrismEnter(origin, direction, triangle, tMin, tMax), best, anyHit)) {
      return best.v;
    }
  }
  for (const brace of braces) {
    if (considerHit(rayCapsuleEnter(origin, direction, brace, tMin, tMax), best, anyHit)) return best.v;
  }
  return best.v < Infinity ? best.v : -1;
}

function rayAabbOverlap(
  origin: Vec3,
  direction: Vec3,
  box: Aabb,
  tMin: number,
  tMax: number
): { tEnter: number; tExit: number } | null {
  let tEnter = -Infinity;
  let tExit = Infinity;
  for (const axis of ['x', 'y', 'z'] as const) {
    const o = origin[axis];
    const d = direction[axis];
    const min = box.min[axis];
    const max = box.max[axis];
    if (Math.abs(d) < 1e-12) {
      if (o < min || o > max) return null;
      continue;
    }
    const inv = 1 / d;
    let t0 = (min - o) * inv;
    let t1 = (max - o) * inv;
    if (t0 > t1) {
      const tmp = t0;
      t0 = t1;
      t1 = tmp;
    }
    if (t0 > tEnter) tEnter = t0;
    if (t1 < tExit) tExit = t1;
    if (tEnter > tExit) return null;
  }
  if (tExit < tMin || tEnter > tMax) return null;
  return { tEnter, tExit };
}

export function analyticBvhHitT(
  origin: Vec3,
  direction: Vec3,
  tMin: number,
  tMax: number,
  planes: readonly AnalyticPlane[],
  triangles: readonly AnalyticTriangle[],
  braces: readonly AnalyticBrace[],
  bvh: AnalyticBvh,
  anyHit = false
): number {
  const best = { v: Infinity };
  let limit = tMax;
  for (const bin of bvh.bins) {
    if (bin.count <= 0) continue;
    const hit = rayAabbOverlap(origin, direction, bin.bounds, tMin, limit);
    if (!hit || hit.tEnter >= best.v) continue;
    for (let i = 0; i < bin.count; i++) {
      const prim = bvh.prims[bin.start + i];
      let t: number | null = null;
      if (prim.kind === ANALYTIC_PRIM_PLANE) {
        t = rayObbEnter(origin, direction, planes[prim.index], tMin, limit);
      } else if (prim.kind === ANALYTIC_PRIM_TRIANGLE) {
        t = rayTrianglePrismEnter(origin, direction, triangles[prim.index], tMin, limit);
      } else {
        t = rayCapsuleEnter(origin, direction, braces[prim.index], tMin, limit);
      }
      if (considerHit(t, best, anyHit)) return best.v;
      if (best.v < limit) limit = best.v;
    }
  }
  return best.v < Infinity ? best.v : -1;
}

export function sampleAnalyticSdf(
  point: Vec3,
  field: BlockLatticeField,
  planes: readonly AnalyticPlane[],
  braces: readonly AnalyticBrace[] = [],
  triangles: readonly AnalyticTriangle[] = []
): number {
  let d = sdBlockLattice(point, field);
  for (const plane of planes) {
    const pd = sdAnalyticPlane(point, plane);
    if (pd < d) d = pd;
  }
  for (const triangle of triangles) {
    const td = sdAnalyticTriangle(point, triangle);
    if (td < d) d = td;
  }
  for (const brace of braces) {
    const bd = sdAnalyticBrace(point, brace);
    if (bd < d) d = bd;
  }
  return d;
}

/**
 * 閉形式距離を球トレースする。0 なら遮った。
 * 開始点はすでに表面の外側（surface bias 済み）を想定する。
 */
export function traceClosedFormVisibility(
  sample: (t: number) => number,
  maxDistance: number,
  maxSteps: number
): number {
  let t = 0;
  const skip = SDF_TRACE_SKIP;
  const startD = Math.max(sample(0), skip);
  for (let step = 0; step < maxSteps; step++) {
    if (t >= maxDistance) return 1;
    const d = sample(t);
    if (d < SDF_TRACE_HIT_EPS && d < startD - skip * 0.5 && (t > skip || d < 0)) return 0;
    t += Math.max(d, SDF_TRACE_HIT_EPS);
  }
  return 1;
}
